import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    FilesetResolver,
    FaceLandmarker,
    NormalizedLandmark,
} from "@mediapipe/tasks-vision";
import { useTranslation } from "react-i18next";

type Props = {
    imageSrc: string;
    loadingText: string;
};

const FACE_CONTOUR_INDEXES = [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
    361, 288, 397, 365, 379, 378, 400, 377, 152,
    148, 176, 149, 150, 136, 172, 58, 132, 93, 234,
    127, 162, 21, 54, 103, 67, 109
];

const FaceScanner = ({ imageSrc, loadingText }: Props) => {
    const [landmarks, setLandmarks] = useState<NormalizedLandmark[] | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const detectFace = async () => {
            const vision = await FilesetResolver.forVisionTasks(
                "https://a.tangent.ai/quiz-inline/mediapipe-models/wasm"
            );

            const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
                baseOptions: {
                    modelAssetPath:
                        "https://a.tangent.ai/quiz-inline/mediapipe-models/face_landmarker/float16/1/face_landmarker.task",
                    delegate: "GPU",
                },
                runningMode: "IMAGE",
                numFaces: 1,
            });

            const image = imageRef.current;
            if (!image) return;

            const faceResult = faceLandmarker.detect(image);
            if (faceResult?.faceLandmarks?.length > 0) {
                setLandmarks(faceResult.faceLandmarks[0]);
            }
        };

        const img = imageRef.current;
        if (img?.complete) {
            detectFace();
        } else if (img) {
            img.onload = detectFace;
        }
    }, [imageSrc]);

    const getFacePath = () => {
        const img = imageRef.current;
        if (!img || !landmarks) return "";

        const rect = img.getBoundingClientRect();

        const points = FACE_CONTOUR_INDEXES.map((index) => {
            const lm = landmarks[index];
            return `${lm.x * rect.width},${lm.y * rect.height}`;
        });

        return `M ${points.join(" L ")} Z`;
    };

    return (
        <div className="tngnt-flex tngnt-flex-col tngnt-items-center tngnt-mt-[64px]">
            <div className="tngnt-relative">
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="Face"
                    className="tngnt-w-[350px] tngnt-h-auto"
                />

                {landmarks && (() => {
                    const img = imageRef.current;
                    if (!img) return null;

                    const rect = img.getBoundingClientRect();
                    const nose = landmarks[1]; // index 1 is the tip of the nose
                    const originX = nose.x * rect.width;
                    const originY = nose.y * rect.height;

                    return (
                        <>
                            {/* SVG FACE CONTOUR GLOW */}
                            <motion.svg
                                className="tngnt-absolute tngnt-top-0 tngnt-left-0 tngnt-w-full tngnt-h-full tngnt-pointer-events-none"
                                initial={{ scale: 0.1, opacity: 1 }}
                                animate={{ scale: 1, opacity: 0 }}
                                transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    repeatType: "loop",
                                    ease: "easeOut",
                                }}
                                style={{
                                    transformOrigin: `${originX}px ${originY}px`,
                                }}
                            >
                                <defs>
                                    <filter id="super-bright-glow" x="-250%" y="-250%" width="600%" height="600%">
                                        {/* Very strong layered blurs */}
                                        <feGaussianBlur in="SourceGraphic" stdDeviation="15" result="blur1" />
                                        <feGaussianBlur in="SourceGraphic" stdDeviation="35" result="blur2" />
                                        <feGaussianBlur in="SourceGraphic" stdDeviation="60" result="blur3" />
                                        <feMerge>
                                            <feMergeNode in="blur1" />
                                            <feMergeNode in="blur2" />
                                            <feMergeNode in="blur3" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                {/* Main glow path */}
                                <path
                                    d={getFacePath()}
                                    fill="none"
                                    stroke="rgba(200, 220, 255, 0.95)"  // Slight bluish white (better on fair skin)
                                    strokeWidth={16}
                                    filter="url(#super-bright-glow)"
                                    opacity={1}
                                />

                                {/* Additional layer to intensify */}
                                <path
                                    d={getFacePath()}
                                    fill="none"
                                    stroke="white"
                                    strokeWidth={12}
                                    filter="url(#super-bright-glow)"
                                    opacity={1}
                                />
                            </motion.svg>
                        </>
                    );
                })()}
            </div>

            <div className="tngnt-flex tngnt-justify-center tngnt-items-center tngnt-m-[20px]">
                <span className="tangent_global_style tngnt-border-solid tngnt-text-[15px]">
                    {loadingText ? loadingText : t('selfieUploadLoading')}
                </span>
            </div>
        </div>
    );
};

export default FaceScanner;
